import { BaseController } from '../../../../../common/base/controller';
import { prepareAccountModelAdapter } from '../../../../../common/adapter/randomDataAccountAdapter';
import { DATA_GENERATOR_CONFIG } from './dataGenerator';
import _ from 'lodash';

export class ReceivingCreateRequestController extends BaseController {
  constructor(
    $scope,
    $compile,
    hotkeys,
    fdFormFieldsService,
    controllerService,
    dataParser,
    ticketsService,
    receivingService,
    receivingCreateRequestService,
    needsConfirmationService,
    hotkeyService,
    modalActionService,
    notificationService,
    dataGenerator,
  ) {
    'ngInject';
    super({
      $scope,
      $compile,
      hotkeys,
      fdFormFieldsService,
      controllerService,
      dataParser,
      ticketsService,
      receivingService,
      receivingCreateRequestService,
      needsConfirmationService,
      hotkeyService,
      modalActionService,
      notificationService,
      dataGenerator,
    });
  }

  get ticketId() {
    return this.$stateParams.ticketId;
  }

  get isNachlass() {
    return this.$stateParams.nachlass && this.$stateParams.nachlass === 'true';
  }

  get isAustriaRB() {
    return this.tenantGroup === 'RBAT';
  }

  get title() {
    return this.isNachlass
      ? 'app.receiving.createRequestNachlass'
      : 'app.receiving.createRequest';
  }

  get ticketOverviewUrlWithParams() {
    return this.$state.href('app.receiving.list', { searchTerm: this.ibans });
  }

  get form() {
    return this.receivingCreateRequestForm;
  }

  get isNotProduction() {
    return !this.envService.isProduction();
  }

  get withFormFields() {
    return !this.isFeatureActive(
      this.FeatureNames.FF_WARRANT_FORM_INSIDE_FRONTEND,
    );
  }

  set form(value) {
    this.receivingCreateRequestForm = value;
  }

  get showAccountswitchDateOnCollisionModal() {
    return this.isFeatureActive(
      this.FeatureNames
        .FF_SHOW_ACCOUNTSWITCH_DATE_ON_CREATE_REQUEST_COLLISION_MODAL,
    );
  }

  get precheckInformCustomerCheckboxes() {
    return this.isFeatureActive(
      this.FeatureNames.FF_PRECHECK_INFORM_CUSTOMER_CHECKBOX_ON_WARRANT_FORM,
    );
  }

  demo() {
    this.fillRandomData();
  }

  $onInit() {
    // Assign
    this.model = {};
    this.breadcrumbs = this.receivingService.states;

    // Apply maybe resolved ticket to the model
    if (this.ticket) {
      angular.extend(this.model, this.ticket.formValues);
    }

    if (this.withFormFields) {
      this.fetchData();
      this.hotkeyService.bindHotkeys(this);
    } else {
      this.formSetup();
      this.hotkeyService.bindHotkeys(this, this.fillRandomData.bind(this));
    }
  }

  fetchData() {
    this.isLoading = true;
    return this.receivingCreateRequestService
      .fetchFields({ nachlass: this.isNachlass })
      .then((fieldGroups) => {
        this.fieldGroups = fieldGroups;
        this.isLoading = false;
      })
      .then(() => this.formSetup());
  }

  formSetup() {
    this.runFormRuler();
    this.loadForm();

    if (this.precheckInformCustomerCheckboxes) {
      [
        'informCustomerDeposits',
        'informCustomerStandingOrders',
        'informCustomerDebits',
      ].forEach((key) => (this.model[key] = true));
    }
  }

  loadForm() {
    const prefilled = this.appCache.get('duplicateTicket');
    if (prefilled) {
      this.model = prefilled;
    }
  }

  clearFormValues() {
    this.appCache.remove('duplicateTicket');
  }

  runFormRuler() {
    const { $scope, $compile, receivingCreateRequestService, model, form } =
      this;
    const injections = { $scope, $compile };

    // Bind rules for the form
    const formRules = this.withFormFields
      ? receivingCreateRequestService.legacyFormRulesForFormFields
      : receivingCreateRequestService.formRules;

    // Watch every model value which has a defined form rule
    _.forEach(formRules, (rule, key) => {
      $scope.$watch(
        `vm.model.${key}`,
        rule.create(form, model, key, injections),
      );
    });
  }

  reset() {
    this.fdFormFieldsService.resetFields(this.form);
    this.clearFormValues();
  }

  // dont remove this. otherwise maybe stored data like 'duplicateTicket' dont get removed when leaving warrant form without submission
  $onDestroy() {
    this.reset();
  }

  submit() {
    const { ticketId } = this;
    this.model.isNachlass = !!this.isNachlass;
    let model = this.prepareModelBeforeValidation(this.model);
    this.isAdvancedSettingsOpen = false;

    this.fdFormFieldsService
      .validate(this.form)
      .busy(this, 'isSubmitBusy')
      .then(() => {
        if (
          !this.modalActionService.showNothingIncludedDialog(
            this,
            model,
            ticketId,
          )
        ) {
          this.submitTicket(model, ticketId, false, true);
        }
      })
      .catch(() => {
        this.isAdvancedSettingsOpen = true;
        this.$scope.$broadcast('form:validate');
      });
  }

  submitTicket(model, ticketId, force, silent) {
    const data = this.prepareModelAfterValidation(model);
    if (!this.isFeatureActive(this.FeatureNames.FF_DUPLICATE_TICKET_WARNING)) {
      force = true;
    }
    this.ticketsService
      .createOrUpdate(data, ticketId, force, silent)
      .then((ticketId) => {
        this.clearFormValues();
        let stateName = this.receivingService.nextState(this.stateIndex);
        this.go(stateName, { ticketId });
      })
      .catch((nope) => {
        if (
          this.isFeatureActive(this.FeatureNames.FF_DUPLICATE_TICKET_WARNING) &&
          nope.status === 412
        ) {
          this.modalActionService.showDuplicatedTicketDialog(
            this,
            model,
            ticketId,
            nope.data,
          );
        }

        if (nope.status === 422) {
          if (nope.data.error === 'newIBANMismatchBIC') {
            this.notificationService.error(
              'receiving.createRequest.form.error.newIBANMismatchBIC',
            );
          }
          if (nope.data.error === 'IBANIsForeign') {
            this.notificationService.error(
              'receiving.createRequest.form.error.IBANIsForeign',
            );
          }

          if (
            nope.data.errors &&
            nope.data.errors[0] &&
            nope.data.errors[0] === 'newIban IBAN validation failed'
          ) {
            this.notificationService.error(
              'receiving.createRequest.form.error.newIBANIsInvalid',
            );
          }
        }
      });
  }

  prepareModelBeforeValidation(model) {
    // Unflatten model keys with a dot inside
    // e.g. {"foo.bar":false} --> {foot:{bar:false}}
    return this.dataParser.unflatten(model);
  }

  fillRandomData() {
    if (this.envService.isProduction()) {
      return;
    }
    this.model.isDemoData = true;
    _.map(DATA_GENERATOR_CONFIG, (field) => {
      const generated = this.dataGenerator.generate(field);
      this.model[field.name] = generated;
    });
  }

  prepareModelAfterValidation(model) {
    return prepareAccountModelAdapter(model);
  }
}
